'use client';

import { useTranslation } from '@/features/i18n';
import { FadeOutOverlay } from '@/ui/FadeOutOverlay';
import { Artwork } from '@/ui/images/Artwork';
import { AssetType } from '@vimond/services/content-discovery';
import { cn } from '@vimond/theme/tailwind';
import { formatSecondsToTimeString, mapToRelativeTime } from '@vimond/utils';
import { getEnvVar } from '@vimond/utils/env';
import Link from 'next/link';

type Props = {
  active: boolean;
  asset: AssetType;
  showInPosterMode?: boolean;
};

export const PlaylistItem = ({
  active,
  asset,
  showInPosterMode = true,
  ...props
}: Props & React.ComponentPropsWithoutRef<'article'>) => {
  const { i18n, t } = useTranslation();
  const { language } = i18n;

  const shouldShowPublishDate =
    getEnvVar('NEXT_PUBLIC_SHOW_PUBLISH_DATE') === 'true';

  return (
    <Link href={`/watch/${asset.id}`}>
      <article
        {...props}
        className={cn(
          'my-px w-full max-w-full overflow-hidden cursor-pointer hover:opacity-90',
          active && 'border-[1px] border-white',
          showInPosterMode
            ? 'aspect-video rounded-md'
            : 'h-[84px] xs-md:h-auto lg:h-[104px] 2xl:h-36',
        )}
      >
        <div
          className={cn(
            'relative flex h-full w-full',
            showInPosterMode ? 'aspect-video flex-col' : 'xs-md:flex-col',
          )}
        >
          {/* Thumbnail */}
          {asset.images?.defaultUrl && (
            <>
              <Artwork
                alt=""
                src={asset.images?.defaultUrl}
                location="portal-carousel"
                fallbackToDefault
                width={255}
                height={145}
                className={cn(
                  'rounded-sm aspect-video',
                  showInPosterMode
                    ? 'object-cover w-full h-full'
                    : 'object-contain w-auto xs-md:w-full bg-slate-950',
                )}
              />

              <div className="relative">
                <div
                  className={cn(
                    'absolute bottom-2 right-2 px-2 py-1 bg-black text-white bg-opacity-75 rounded-sm font-extrabold text-xs z-10',
                  )}
                >
                  {formatSecondsToTimeString(asset.duration)}
                </div>
              </div>
            </>
          )}

          {showInPosterMode && <FadeOutOverlay />}

          {/* Title & publishing date */}
          <div
            className={cn(
              'flex flex-col justify-start',
              showInPosterMode
                ? 'flex-shrink-0 text-shadow absolute bottom-0 left-2 right-2 text-left text-white lg:bottom-4 lg:left-4 lg:right-4'
                : 'w-[50%] xs-md:w-auto lg:w-auto py-2 px-3 xs-md:px-1 lg:px-4 md:pt-2 h-full',
            )}
          >
            <h2 className="text-ellipsis text-sm xs-lg:text-base lg:text-lg font-bold line-clamp-2 2xl:line-clamp-3">
              {asset.title}
            </h2>
            {shouldShowPublishDate && asset.publishing.start && (
              <div className="mt-1">
                <span className="text-xs lg:text-sm">
                  {mapToRelativeTime(asset.publishing.start, t, language)}
                </span>
              </div>
            )}
          </div>
        </div>
      </article>
    </Link>
  );
};
